import axios from "axios";
import logger from "../../utilitis/logger";

// azure backend
const api = axios.create({
  baseURL: process.env.REACT_APP_BASEURL,
  timeout: 60000,
  headers: {
    Accept: "application/json",
  },
  withCredentials: true,
});


// Modify the request interceptor
api.interceptors.request.use(
  (config) => {
    config.withCredentials = true;

    // Don't set Content-Type for FormData
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Generic API service methods
export const apiService = {
  get: async (url) => {
    try {
      const response = await api.get(url);
      logger.info("API service methods get:", response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  post: async (url, data) => {
    try {
      const response = await api.post(url, data);
      logger.info("API service methods post:", response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Device endpoints instance — uses same baseURL as main api
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_BASEURL,
  timeout: 60000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Protected endpoints using main api instance
export const signOut = async () => {
  try {
    await publicApi.post("/device/signout");
  } catch (error) {
    logger.error("Error signing out:", error);
    throw error;
  }
};

export const fetchDataDevice = async (encode) => {
  try {
    const response = await publicApi.post(`/device/${encode}`);
    return response.data;
  } catch (error) {
    // Log full error details for debugging
    logger.error("fetchDataDevice error:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      config: {
        url: error.config?.url,
        method: error.config?.method
      }
    });
    
    // Check for QR code not found error
    if (error.response?.status === 404 && error.response?.data?.error === "QR_CODE_NOT_FOUND") {
      logger.info("QR Code not found:", error.response.data.scannedCode);
      const qrNotFoundError = new Error(error.response.data.message);
      qrNotFoundError.code = "QR_CODE_NOT_FOUND";
      qrNotFoundError.scannedCode = error.response.data.scannedCode;
      throw qrNotFoundError;
    }
    
    // Check for serial number not found error
    if (error.response?.status === 400 && error.response?.data?.error === "NO_SERIAL_NUMBER") {
      logger.info("Serial number not found for QR code");
      const serialError = new Error(error.response.data.message);
      serialError.code = "NO_SERIAL_NUMBER";
      throw serialError;
    }
    
    // Re-throw other errors
    logger.error("Unhandled error in fetchDataDevice");
    throw error;
  }
};

export const lookupDeviceBySerial = async (deviceNumber) => {
  try {
    const response = await publicApi.get(`/device/lookup/${deviceNumber}`);
    return response.data;
  } catch (error) {
    logger.error("Error looking up device by serial:", error);
    throw error;
  }
};

export const createTicket = async (ticketData) => {
  try {
    // Check if we have any images to handle
    const hasImages = ticketData.images && ticketData.images.length > 0;

    // If we have images, use FormData to send both files and data
    if (hasImages) {
      const formData = new FormData();

      // Append all ticket data fields
      formData.append("SERNUM", ticketData.serialNumber);
      formData.append("CUSTNAME", ticketData.customerNumber);
      formData.append("CALLTYPECODE", ticketData.callType);
      formData.append("ELDN_UPDSOURCE", ticketData.updateSource);
      formData.append("ELDN_UPDSRCPHONEMAIL", ticketData.contactInfo);
      formData.append("TEXT", ticketData.description);
      formData.append("URGENCY", ticketData.urgency);
      formData.append("EMAIL", ticketData.email || "");
      formData.append("ADDRESS_CHANGE_REQUEST", ticketData.addressChangeRequest || "");
      formData.append("CONTACT_NAME_CHANGE_REQUEST", ticketData.contactNameChangeRequest || "");
      formData.append("CONTACT_PHONE_CHANGE_REQUEST", ticketData.contactPhoneChangeRequest || "");
      formData.append("CONTACT_EMAIL_CHANGE_REQUEST", ticketData.contactEmailChangeRequest || "");

      // Append images
      ticketData.images.forEach((image, index) => {
        if (index < 5) {
          // Limit to 5 images total
          formData.append("images", image);
        }
      });

      // Important: Remove Content-Type from headers
      const response = await api.post("device/createticket", formData, {
        headers: {
          Accept: "application/json",
        },
      });

      return response.data;
    }

    // If no images, send regular JSON data
    const response = await api.post("device/createticket", {
      SERNUM: ticketData.serialNumber,
      CUSTNAME: ticketData.customerNumber,
      CALLTYPECODE: ticketData.callType,
      ELDN_UPDSOURCE: ticketData.updateSource,
      ELDN_UPDSRCPHONEMAIL: ticketData.contactInfo,
      TEXT: ticketData.description,
      URGENCY: ticketData.urgency,
      EMAIL: ticketData.email || "",
      IMAGES: [], // Empty array when no images
      ADDRESS_CHANGE_REQUEST: ticketData.addressChangeRequest || "",
      CONTACT_NAME_CHANGE_REQUEST: ticketData.contactNameChangeRequest || "",
      CONTACT_PHONE_CHANGE_REQUEST: ticketData.contactPhoneChangeRequest || "",
      CONTACT_EMAIL_CHANGE_REQUEST: ticketData.contactEmailChangeRequest || "",
    });

    logger.info("API Response createTicket:", response.data);
    return response.data;
  } catch (error) {
    if (error.response?.status === 413) {
      throw new Error("File size too large. Please reduce the image size.");
    }

    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    logger.error("Error creating ticket:", error);
    throw error;
  }
};
